/**
 * Run the link pass (PLAN-INGREDIENT-LINKS D1) once the workspace has settled.
 *
 * ## Why here, and why on every load
 *
 * The pass is ordinary commands (`planIngredientLinks`), so it has to go
 * through the store like a keystroke: undo, persistence and sync all see it.
 * It is idempotent — anything already linked is skipped — so there is no flag
 * to persist and nothing to get wrong about *whether* it has run; the cost of
 * running it on every load is one scan of the lots and rows, which is cheaper
 * than a setting would be to keep honest across devices.
 *
 * ## Why it waits for `synced`
 *
 * A signed-in client's local copy may be behind the server until the first
 * catch-up. A link computed against a lot another device has since deleted
 * would be pushed, refused by the Durable Object, and reported to the brewer
 * as an edit that could not be saved — for an edit they never made. So the
 * pass waits until the sync client reports `synced` (caught up, outbox empty)
 * and runs against the confirmed state. A guest has no server to be behind, so
 * `guest` is ready at once. `offline`, `connecting` and `syncing` wait; the
 * first time the status reaches a settled value the pass runs, and that is the
 * only time it runs on this mount.
 *
 * ## One undo step, and the latch that keeps it undone
 *
 * Dispatched through `dispatchAll` with one label, so a brewer who presses ⌘Z
 * straight after opening the app undoes the whole pass at once rather than one
 * lot per press — and gets it back on the next load, which is the honest
 * consequence of an idempotent pass with no persisted flag.
 *
 * That "gets it back" must happen on the *next load*, never inside the session
 * the undo happened in, and a `ref` latch is what guarantees it: `done` is set
 * before the first dispatch and never cleared, so the effect body is inert for
 * the rest of the mount however many times React re-runs it. A ref rather than
 * state because nothing renders differently for having run — flipping state
 * here would buy a re-render nobody reads.
 *
 * The latch is load-bearing, not belt-and-braces, and the reasoning that says
 * otherwise is wrong in a way worth spelling out. It is tempting to argue that
 * the effect's dependencies — `settled` and the store instance — are unmoved by
 * an undo, so the effect cannot re-run. That holds for a guest, whose status is
 * pinned at `guest` for the tab's life. It is false for a signed-in brewer:
 * `SyncClient` reports `syncing` whenever its outbox is non-empty, and *every*
 * announced command — including the inverses `WorkspaceStore.undo()` announces
 * — puts one there synchronously. So a signed-in session runs
 * `synced → syncing → synced` around every edit, `settled` goes false and true
 * again with it, and an unlatched effect would re-run its body: a full scan of
 * every lot and every row after every acknowledged edit for the life of the
 * tab, and — worse — an undo of the pass that the very next round trip puts
 * straight back, leaving the brewer unable to get past that one undo entry.
 * The latch is what makes the documented behaviour ("waits for a settled
 * workspace, and gets another go on the next load") true for both kinds of
 * session rather than only for guests.
 */

import { planIngredientLinks } from '@beerwright/commands';
import { useEffect, useRef } from 'react';
import { useSyncStatus } from '@/sync/sync-context';
import { useHydrated, useWorkspaceStore } from './workspace-context';

/** The undo label the pass is filed under. */
export const LINK_PASS_SUMMARY = 'Link your ingredients';

export function useIngredientLinkBackfill(): void {
  const store = useWorkspaceStore();
  const hydrated = useHydrated();
  const status = useSyncStatus();
  const settled = hydrated && (status === 'guest' || status === 'synced');
  const done = useRef(false);

  useEffect(() => {
    if (!settled || done.current) return;
    // Latched before the dispatch, not after: `dispatchAll` notifies
    // subscribers synchronously, so a component re-rendering from that
    // notification could re-enter this effect while it is still on the stack.
    done.current = true;
    const steps = planIngredientLinks(store.getState());
    if (steps.length === 0) return;
    const result = store.dispatchAll(steps, LINK_PASS_SUMMARY);
    // A refusal here is a bug in the planner, not something the brewer did;
    // it is logged rather than toasted because there is nothing for them to act
    // on, and the next load will try again against whatever state is then true.
    if (!result.ok) console.warn('[beerwright] ingredient link pass refused:', result.error);
  }, [settled, store]);
}
